import React, {useState, createContext, useEffect} from 'react';
import * as PropTypes from 'prop-types';

import ProductSelection from "./product_selection/ProductSelection";
import Checkout from "./checkout/Checkout";
import Confirmation from "./confirmation/Confirmation";

import CountriesPropTypes from "../prop-types/CountriesPropTypes";
import {PORTALS, PRODUCT_CATEGORIES, PURCHASE_FLOWS} from "../../../constants";
import LineItem from "../utils/LineItem";
import ChooseModeStage from "./choose_mode/ChooseModeStage";

export const buyNowContext = createContext(null);

export const shippingMethods = {
  standard: { type: "standard", get label() { return `Regular (${this.timeFrame})`; }, timeFrame: "5-7 business days" },
  expedited: { type: "expedited", get label() { return `Expedited (${this.timeFrame})`; }, timeFrame: "2-3 business days" }
};

export const recipientType = {
  me: { type: "me"}, recipient: { type: "recipient"}
};

/**
 * This component is a host for the 3 purchase stages (product selection, shipping info, and credit card info).
 */
const BuyNow = ({ portal, purchaseFlow, promotional, scaleBpBundleProducts, scaleProducts, bpDeviceProducts, thermometerProducts, planProducts, adminAccessProducts, countries, salesTaxRates, isProduction, referralCode, referralCodeValid, plansConfiguration, selectedPlanId, urlPromoCode, adminReferralTargetCode, abTestingPage }) => {
  // Some purchase flows may start with an empty order and the user must select which items they want to purchase.
  // Other purchase flows may start with items already selected, meaning the user can modify the selection or just proceed to the next page without making any changes.
  let initialLineItems = [];
  if (purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_END_USER || purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_PARENT || purchaseFlow === PURCHASE_FLOWS.SHAPA_HEALTH_END_USER) {
    initialLineItems = [
      new LineItem({ product: scaleBpBundleProducts[0], quantity: 1 }),
      new LineItem({ product: planProducts[0], quantity: 1 })
    ];

    if (purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_PARENT)
      initialLineItems.push(new LineItem({ product: adminAccessProducts[0], quantity: 1 }));
  } else if (purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_CLINICIAN || purchaseFlow === PURCHASE_FLOWS.SHAPA_HEALTH_CLINICIAN) {
    initialLineItems = [
      new LineItem({ product: adminAccessProducts[0], quantity: 1 })
    ];
  } else if (purchaseFlow === PURCHASE_FLOWS.SHAPA_END_USER) {
    let initialSelectedPlan = planProducts.find(p => p.default);

    // If there is a selected plan in the query string (ex. the user selected a plan on the homepage), use that instead of the default.
    if (selectedPlanId) {
      const queryStringSelectedPlanIndex = planProducts.findIndex((plan) => plan.id === parseInt(selectedPlanId));
      const queryStringSelectedPlan = planProducts[queryStringSelectedPlanIndex];
      if (queryStringSelectedPlan)
        initialSelectedPlan = queryStringSelectedPlan;
    }

    initialLineItems = [
      new LineItem({ product: scaleProducts[0], quantity: 1 }),
      new LineItem({ product: initialSelectedPlan, quantity: 1 }),
    ];
  } else if (purchaseFlow === PURCHASE_FLOWS.SHAPA_BP_ADDON || purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_BP_ADDON) {
    initialLineItems = [
      new LineItem({ product: bpDeviceProducts[0], quantity: 1 }),
    ];
  } else if (purchaseFlow === PURCHASE_FLOWS.SHAPA_THERMOMETER_ADDON || purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_THERMOMETER_ADDON) {
    initialLineItems = [
      new LineItem({ product: thermometerProducts[0], quantity: 1 }),
    ];
  }

  const CLINICIAN_MODES = { NEW_ORGANIZATION: "NEW_ORGANIZATION", EXISTING_ORGANIZATION: "EXISTING_ORGANIZATION" };
  const PURCHASE_STAGES = { CHOOSE_MODE: "CHOOSE_MODE", PRODUCT_SELECTION: "PRODUCT_SELECTION", SHIPPING: "SHIPPING", PAYMENT: "PAYMENT", REVIEW: "REVIEW", CONFIRMATION: "CONFIRMATION" };
  const INITIAL_PURCHASE_STATE = purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_CLINICIAN || purchaseFlow === PURCHASE_FLOWS.SHAPA_HEALTH_CLINICIAN ? PURCHASE_STAGES.CHOOSE_MODE : PURCHASE_STAGES.PRODUCT_SELECTION;
  const [promoCode, setPromoCode] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [purchaseStage, setPurchaseStageDirect] = useState(INITIAL_PURCHASE_STATE);
  const [clinicianMode, setClinicianMode] = useState(CLINICIAN_MODES.NEW_ORGANIZATION);
  const [order, setOrder] = useState(null);
  const [lineItems, setLineItems] = useState(initialLineItems);
  const [npi, setNpi] = useState(null);
  const [deferredUserCreate, setDeferredUserCreate] = useState(false);

  const setPurchaseStage = val => {
    setPurchaseStageDirect(val);
    history.pushState({ stage: val }, null, "");

    // Each time we set the purchase stage, we'll scroll to the top of the window.
    // We've actually had people complain that sometimes they only see the footer and it has been impossible to reproduce for us, even with Guy going to one customer's house to use their phone and computer.
    // I have a suspicion that they have a small screen or window and sometimes get to a purchase stage that is scrolled too far from the previous stage and they don't know they have to scroll up.
    // So, this is very important code. Don't remove unless replacing with a better solution.
    window.scrollTo(0, 0);
  };

  /**
   * This method will take an array of line items and for each one, first search for an existing line item of the same product category (ex. device or membership) and device type (ex. scale or BP device)
   * and update the line item if found. If an existing line item isn't found, the given line item will simply be appended.
   * @param {LineItem[]} lineItemUpdates
   */
  const setLineItemsByCategory = lineItemUpdates => {
    const newLineItems = [...lineItems];

    lineItemUpdates.forEach(lineItemUpdate => {
      const lineItemObj = new LineItem(lineItemUpdate);

      const existingLineItemIndex = newLineItems.findIndex(li => li.product.category === lineItemObj.product.category);
      if (existingLineItemIndex >= 0)
        newLineItems[existingLineItemIndex] = lineItemObj;
      else
        newLineItems.push(lineItemObj);
    });

    // Sort line items such that memberships appear after any other products such as scales, BP devices, and admin access.
    newLineItems.sort((a, b) => {
      const aOrder = a.product.category === PRODUCT_CATEGORIES.MEMBERSHIP ? 1 : 0;
      const bOrder = b.product.category === PRODUCT_CATEGORIES.MEMBERSHIP ? 1 : 0;
      return aOrder - bOrder;
    });

    // Any time the line items are updated, we should reset the promo code, as we no longer know if the promo code is valid for the new product selections.
    setPromoCode(null);

    setLineItems(newLineItems);
  };

  const removeLineItems = existingLineItems => {
    setLineItems(lineItems.filter(li => {
      if (Array.isArray(existingLineItems))
        return !existingLineItems.includes(li);

      return li !== existingLineItems;
    }));

    // Any time the line items are updated, we should reset the promo code, as we no longer know if the promo code is valid for the new product selections.
    setPromoCode(null);
  };

  const [shippingStageState, setShippingStageState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address1: '',
    address2: '',
    phone: '',
    country: countries["US"],
    city: '',
    state: '',
    zipCode: '',
    password: '',
    passwordConfirmation: '',
    organizationName: '',
    organizationCode: '',
    shippingMethod: shippingMethods.standard,
    isArrivalPhoneCallRequested: false
  });

  const [paymentStageState, setPaymentStageState] = useState({
      creditCardNumber: '',
      nameOnCard: '',
      creditCardDate: '',
      maskChar: '',
      cvv: '',
      isUsingBillingAddress: false,
      paymentInputAddress: '',
      paymentInputAddress2: '',
      paymentInputCountry: countries["US"],
      paymentInputCity: '',
      paymentInputState: '',
      paymentInputZip: '',
    }
  );

  const [giftSenderInformation, setGiftSenderInformation] = useState({
      name: '',
      email: '',
      message: '',
      giftConfirmationEmail: recipientType.me,
      isGift: false,
    }
  );

  // This object will give useful information about the app/environment depending on the purchase flow portal.
  let appInfo;
  if (portal === PORTALS.CLEARSTEP)
    appInfo = { name: "MyClearStep", supportEmail: 'support@myclearstep.com', clientPurchaseURL: 'https://store.myclearstep.com/orders/buy-now', iosStoreUrl: 'https://apps.apple.com/us/app/clearstep/id1510599421', androidStoreUrl: 'https://play.google.com/store/apps/details?id=com.shapau.android&hl=en_US&gl=US' };
  else if (portal === PORTALS.SHAPA_HEALTH)
    appInfo = { name: "Shapa", supportEmail: 'support@shapa.me', clientPurchaseURL: 'https://www.myshapa.com/orders/buy-now', iosStoreUrl: 'https://apps.apple.com/us/app/shapa-inc/id1053767884', androidStoreUrl: 'https://play.google.com/store/apps/details?id=com.shapa.android' };
  else
    appInfo = { name: "Shapa", supportEmail: 'support@shapa.me', clientPurchaseURL: 'https://www.myshapa.com/orders/buy-now', iosStoreUrl: 'https://apps.apple.com/us/app/shapa-inc/id1053767884', androidStoreUrl: 'https://play.google.com/store/apps/details?id=com.shapa.android' };

  const contextValue = {
    appInfo,
    PURCHASE_STAGES,
    CLINICIAN_MODES,
    promotional,
    totalPrice,
    order,
    setOrder,
    setTotalPrice,
    selectedPlanId,
    purchaseStage,
    scaleBpBundleProducts,
    scaleProducts,
    bpDeviceProducts,
    thermometerProducts,
    planProducts,
    adminAccessProducts,
    setPurchaseStage,
    shippingStageState,
    setShippingStageState,
    paymentStageState,
    setPaymentStageState,
    promoCode,
    setPromoCode,
    salesTaxRates,
    isProduction,
    referralCode,
    referralCodeValid,
    plansConfiguration,
    giftSenderInformation,
    setGiftSenderInformation,
    urlPromoCode,
    adminReferralTargetCode,
    abTestingPage,
    quantitiesEnabled: purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_CLINICIAN || purchaseFlow === PURCHASE_FLOWS.SHAPA_HEALTH_CLINICIAN,
    portal,
    purchaseFlow,
    lineItems,
    setLineItemsByCategory,
    setLineItems,
    removeLineItems,
    npi,
    setNpi,
    deferredUserCreate,
    setDeferredUserCreate,
    clinicianMode,
    setClinicianMode
  };

  useEffect(() => {
    history.replaceState({ stage: INITIAL_PURCHASE_STATE }, null, "");
  }, []);

  const renderContent = () => {
    let content = null;
    const navBar = document.getElementById("nav-bar");
    const buyNowContainer= document.getElementById("buy-now-container");
    if (purchaseStage === PURCHASE_STAGES.PRODUCT_SELECTION) {
      const timeoutId = setTimeout(() => { // we need to setTimeout at first to get NavBar values after it gets rendered.
        navBar.style.visibility = "visible";
        buyNowContainer.style.marginTop = `${navBar.offsetHeight + navBar.offsetTop}px`;
        clearTimeout(timeoutId);
      }, 200);
      content = <ProductSelection />;
    }
    else if (purchaseStage === PURCHASE_STAGES.CHOOSE_MODE) {
      const timeoutId = setTimeout(() => { // we need to setTimeout at first to get NavBar values after it gets rendered.
        navBar.style.visibility = "visible";
        buyNowContainer.style.marginTop = `${navBar.offsetHeight + navBar.offsetTop}px`;
        clearTimeout(timeoutId);
      }, 200);
      content = <ChooseModeStage />;
    }
    else if ([PURCHASE_STAGES.SHIPPING, PURCHASE_STAGES.PAYMENT, PURCHASE_STAGES.REVIEW].includes(purchaseStage)) {
      const timeoutId = setTimeout(() => { // we need to setTimeout at first to get NavBar values after it gets rendered.
        navBar.style.visibility = "hidden";
        const orderNav = document.getElementById("order-nav");
        buyNowContainer.style.marginTop = `${orderNav.offsetHeight}px`;
        clearTimeout(timeoutId);
      }, 200);
      content = <Checkout countries={countries} />;
    }
    else if (purchaseStage === PURCHASE_STAGES.CONFIRMATION) {
      content = <Confirmation />;
    }
    return content;
  };

  window.onpopstate = function(event) {
    // Don't let the user to change state if they are on the confirmation page.
    // If we allowed this, a user could potentially create a duplicate order trying to get back to the confirmation page.
    if (purchaseStage === PURCHASE_STAGES.CONFIRMATION)
      return;

    if (event.state.stage === PURCHASE_STAGES.PRODUCT_SELECTION)
      setPurchaseStageDirect(PURCHASE_STAGES.PRODUCT_SELECTION);
    else if (event.state.stage === PURCHASE_STAGES.SHIPPING)
      setPurchaseStageDirect(PURCHASE_STAGES.SHIPPING);
    else if (event.state.stage === PURCHASE_STAGES.PAYMENT)
      setPurchaseStageDirect(PURCHASE_STAGES.PAYMENT);
    else if (event.state.stage === PURCHASE_STAGES.REVIEW)
      setPurchaseStageDirect(PURCHASE_STAGES.REVIEW);
    else if (event.state.stage === PURCHASE_STAGES.CHOOSE_MODE)
      setPurchaseStageDirect(PURCHASE_STAGES.CHOOSE_MODE);
  };

  return (
    <div>
      <buyNowContext.Provider value={contextValue}>
        {renderContent()}
      </buyNowContext.Provider>
    </div>
  );
};

BuyNow.propTypes = {
  portal: PropTypes.string.isRequired,
  purchaseFlow: PropTypes.string.isRequired,
  promotional: PropTypes.object,
  scaleBpBundleProducts: PropTypes.array.isRequired,
  scaleProducts: PropTypes.array.isRequired,
  bpDeviceProducts: PropTypes.array.isRequired,
  thermometerProducts: PropTypes.array.isRequired,
  planProducts: PropTypes.array.isRequired,
  adminAccessProducts: PropTypes.array.isRequired,
  salesTaxRates: PropTypes.array.isRequired,
  isProduction: PropTypes.bool.isRequired,
  countries: CountriesPropTypes.isRequired,
  referralCode: PropTypes.string,
  referralCodeValid: PropTypes.bool.isRequired,
  plansConfiguration: PropTypes.object.isRequired,
  urlPromoCode: PropTypes.string,
  adminReferralTargetCode: PropTypes.string,
  abTestingPage: PropTypes.bool.isRequired
};

// solution for using hooks in parent component https://github.com/shakacode/react_on_rails/issues/1198#issuecomment-483784251
export default BuyNow;
